import React from 'react';
import {formatDate} from '../utils/dateTime';
import {useNavigate, useOutletContext} from 'react-router-dom';
import {SparklesIcon} from '../components/icons';
import type {ItemPageContext} from './ClothingItemDetail';
import {itemStatusLabel, PURPOSE_LABELS} from '../support/itemLifecycle';
import {pluralForm} from '../utils/plural';

// ─── Helpers ───────────────────────────────────────────────────────────────

const fmtDate = (s: string | null | undefined): string | null =>
    s ? formatDate(s) : null;

// ─── Card wrapper ──────────────────────────────────────────────────────────

const Card: React.FC<{title: string; children: React.ReactNode}> = ({title, children}) => (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            {title}
        </h3>
        {children}
    </div>
);

// ─── Detail row ────────────────────────────────────────────────────────────

const DetailRow: React.FC<{label: string; value: string | null | undefined}> = ({label, value}) => {
    if (value == null || value === '') return null;
    return (
        <div className="flex justify-between gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</span>
            <span className="text-sm text-gray-900 dark:text-gray-100 text-right">{value}</span>
        </div>
    );
};

// ─── Stat block ────────────────────────────────────────────────────────────

const StatBlock: React.FC<{value: React.ReactNode; label: string}> = ({value, label}) => (
    <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 text-center">
        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</div>
    </div>
);

// ─── Circular progress SVG ─────────────────────────────────────────────────

const CircularProgress: React.FC<{value: number; max: number}> = ({value, max}) => {
    const r = 28;
    const circ = 2 * Math.PI * r;
    const pct = Math.max(0, Math.min(1, value / max));
    const offset = circ * (1 - pct);

    const color =
        value >= 8 ? '#16a34a' :
        value >= 6 ? '#eab308' :
        value >= 4 ? '#f97316' : '#dc2626';

    return (
        <svg width={72} height={72} viewBox="0 0 72 72" className="flex-shrink-0">
            <circle
                cx="36" cy="36" r={r}
                fill="none"
                stroke="currentColor"
                strokeWidth="6"
                className="text-gray-200 dark:text-gray-700"
            />
            <circle
                cx="36" cy="36" r={r}
                fill="none"
                stroke={color}
                strokeWidth="6"
                strokeDasharray={circ}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform="rotate(-90 36 36)"
            />
            <text
                x="36" y="40"
                textAnchor="middle"
                fontSize="14"
                fontWeight="700"
                fill={color}
            >
                {value.toFixed(1)}
            </text>
        </svg>
    );
};

// ─── Occasion radar (SVG pentagon) ────────────────────────────────────────

const OCCASION_LABELS = ['Повседневность', 'Работа', 'Спорт', 'Вечер/Выход', 'Прогулка'];

const polarToXY = (angleDeg: number, r: number, cx: number, cy: number) => {
    const rad = (angleDeg - 90) * (Math.PI / 180);
    return {x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad)};
};

interface OccasionRatings {
    daily: number | null;
    office: number | null;
    sport: number | null;
    evening: number | null;
    outdoor: number | null;
}

const OccasionRadar: React.FC<{ratings: OccasionRatings}> = ({ratings}) => {
    const size = 220;
    const cx = size / 2;
    const cy = size / 2;
    const maxR = 76;
    const values = [
        (ratings.daily ?? 0) / 10,
        (ratings.office ?? 0) / 10,
        (ratings.sport ?? 0) / 10,
        (ratings.evening ?? 0) / 10,
        (ratings.outdoor ?? 0) / 10,
    ];
    const rawVals = [
        ratings.daily ?? 0,
        ratings.office ?? 0,
        ratings.sport ?? 0,
        ratings.evening ?? 0,
        ratings.outdoor ?? 0,
    ];
    const n = 5;
    const angles = Array.from({length: n}, (_, i) => (360 / n) * i);
    const rings = [0.25, 0.5, 0.75, 1];
    const axisPoints = angles.map(a => polarToXY(a, maxR, cx, cy));
    const dataPoints = values.map((v, i) => polarToXY(angles[i], v * maxR, cx, cy));
    const polygonPts = (pts: {x: number; y: number}[]) => pts.map(p => `${p.x},${p.y}`).join(' ');

    return (
        <div className="flex flex-col items-center gap-2">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} overflow="visible">
                {rings.map(r => (
                    <polygon
                        key={r}
                        points={polygonPts(angles.map(a => polarToXY(a, r * maxR, cx, cy)))}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="0.5"
                        className="text-gray-300 dark:text-gray-600"
                    />
                ))}
                {axisPoints.map((pt, i) => (
                    <line key={i} x1={cx} y1={cy} x2={pt.x} y2={pt.y}
                        stroke="currentColor" strokeWidth="0.5"
                        className="text-gray-300 dark:text-gray-600"/>
                ))}
                <polygon
                    points={polygonPts(dataPoints)}
                    fill="rgba(34,197,94,0.15)"
                    stroke="rgb(34,197,94)"
                    strokeWidth="1.5"
                />
                {dataPoints.map((pt, i) => (
                    <circle key={i} cx={pt.x} cy={pt.y} r={3} fill="rgb(34,197,94)"/>
                ))}
                {axisPoints.map((pt, i) => {
                    const dx = pt.x - cx;
                    const dy = pt.y - cy;
                    const len = Math.sqrt(dx * dx + dy * dy) || 1;
                    const lx = pt.x + (dx / len) * 18;
                    const ly = pt.y + (dy / len) * 14;
                    return (
                        <g key={i}>
                            <text x={lx} y={ly - 5} textAnchor="middle"
                                fontSize="7.5" className="fill-gray-500 dark:fill-gray-400">
                                {OCCASION_LABELS[i]}
                            </text>
                            <text x={lx} y={ly + 6} textAnchor="middle"
                                fontSize="8" fontWeight="600" className="fill-gray-700 dark:fill-gray-200">
                                {rawVals[i].toFixed(1)}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

// ─── Season pill ───────────────────────────────────────────────────────────

const SeasonPill: React.FC<{label: string; active: boolean}> = ({label, active}) => (
    <span className={`px-3 py-1 rounded-full text-sm border ${
        active
            ? 'bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 border-brand-200 dark:border-brand-700'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700'
    }`}>
        {label}
    </span>
);

// ─── Main component ────────────────────────────────────────────────────────

const ItemDetailsTab: React.FC = () => {
    const {item, itemId, aiProfile} = useOutletContext<ItemPageContext>();
    const navigate = useNavigate();

    const [showDesc, setShowDesc] = React.useState(false);

    const seasons = (item.seasonNames ?? '').split(/[,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    const defects = (item.defectNames ?? '').split(/[,;]+/).map(s => s.trim()).filter(Boolean);

    const aiRating = aiProfile?.aiRatingOverall ?? null;
    const ratingValue = aiRating ?? 0;

    const ratingLabel =
        ratingValue >= 8 ? 'Рекомендуется оставить' :
        ratingValue >= 6 ? 'Можно оставить' :
        ratingValue >= 4 ? 'Рассмотрите замену' : 'Рекомендуем пересмотреть';

    const ratingColor =
        ratingValue >= 8 ? 'text-green-600 dark:text-green-400' :
        ratingValue >= 6 ? 'text-yellow-600 dark:text-yellow-400' :
        ratingValue >= 4 ? 'text-orange-600 dark:text-orange-400' : 'text-red-600 dark:text-red-400';

    const showAiRecommendation = aiProfile?.status === 'COMPLETED' && aiProfile.itemAdvice && aiRating != null;

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">

            {/* ── Card 1: Параметры ─────────────────────────────────────── */}
            <Card title="Параметры">
                <div className="space-y-2">
                    <DetailRow label="Категория" value={item.categoryName}/>
                    <DetailRow label="Назначение" value={item.purpose ? (PURPOSE_LABELS[item.purpose] ?? item.purpose) : null}/>
                    <DetailRow label="Статус" value={itemStatusLabel(item)}/>
                    <DetailRow label="Крой" value={item.fitName}/>
                    <DetailRow label="Материал" value={item.materialNames}/>
                    <DetailRow label="Цвет" value={item.colorNames}/>
                    <DetailRow label="Слои" value={item.layerNames}/>
                    <DetailRow label="Уровень тепла" value={item.warmthLevelName}/>
                    <DetailRow label="Стиль" value={item.styleNames}/>
                    <DetailRow label="Поводы" value={item.occasionNames}/>
                    <DetailRow label="Погода" value={item.weatherSuitabilityNames}/>
                    {item.waterProof && <DetailRow label="Водонепроницаемая" value="Да"/>}
                    <DetailRow label="Дата покупки" value={fmtDate(item.purchaseDate) ?? undefined}/>
                    <DetailRow label="Дата утилизации" value={fmtDate(item.disposalDate) ?? undefined}/>
                </div>
                {item.description && (
                    <div>
                        {showDesc ? (
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap mt-2">
                                {item.description}
                            </p>
                        ) : null}
                        <button
                            onClick={() => setShowDesc(v => !v)}
                            className="text-xs text-primary dark:text-primary-light hover:underline mt-1"
                        >
                            {showDesc ? 'Скрыть описание' : 'Показать описание'}
                        </button>
                    </div>
                )}
            </Card>

            {/* ── Card 2: Использование ─────────────────────────────────── */}
            <Card title="Использование">
                {/* Occasion radar (shows only when AI profile has been regenerated with occasion data) */}
                {aiProfile?.status === 'COMPLETED' &&
                 (aiProfile.occasionRatingDaily != null || aiProfile.occasionRatingOffice != null ||
                  aiProfile.occasionRatingSport != null || aiProfile.occasionRatingEvening != null ||
                  aiProfile.occasionRatingOutdoor != null) && (
                    <OccasionRadar ratings={{
                        daily: aiProfile.occasionRatingDaily ?? null,
                        office: aiProfile.occasionRatingOffice ?? null,
                        sport: aiProfile.occasionRatingSport ?? null,
                        evening: aiProfile.occasionRatingEvening ?? null,
                        outdoor: aiProfile.occasionRatingOutdoor ?? null,
                    }}/>
                )}
                {/* Always show base stats */}
                <div className="grid grid-cols-3 gap-2">
                    <StatBlock value={item.totalWears ?? 0}
                               label={pluralForm(item.totalWears ?? 0, 'носка', 'носки', 'носок')}/>
                    <StatBlock value={item.totalWashes ?? 0}
                               label={pluralForm(item.totalWashes ?? 0, 'стирка', 'стирки', 'стирок')}/>
                    <StatBlock
                        value={aiRating != null ? aiRating.toFixed(1) : '—'}
                        label="AI рейтинг"
                    />
                </div>
                {item.lastWornAt && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Последняя носка:{' '}
                        <strong className="text-gray-900 dark:text-gray-100">{fmtDate(item.lastWornAt)}</strong>
                    </p>
                )}
            </Card>

            {/* ── Card 3: Дефекты ───────────────────────────────────────── */}
            <Card title="Дефекты">
                {defects.length === 0 ? (
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                        <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        Серьёзных дефектов нет
                    </div>
                ) : (
                    <div className="space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                            {defects.map((d, i) => (
                                <span
                                    key={i}
                                    className="px-2 py-0.5 rounded-full text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800"
                                >
                                    {d}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                {item.conditionName && (
                    <div className="pt-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Состояние: </span>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200">
                            {item.conditionName}
                        </span>
                    </div>
                )}
            </Card>

            {/* ── Card 4: Сезоны + AI ───────────────────────────────────── */}
            <Card title="Сезоны">
                <div className="flex flex-wrap gap-2">
                    {(['весна', 'лето', 'осень', 'зима'] as const).map(season => {
                        const labelMap: Record<string, string> = {
                            весна: 'Весна', лето: 'Лето', осень: 'Осень', зима: 'Зима',
                        };
                        const active = seasons.some(s =>
                            s === season ||
                            s.startsWith(season.slice(0, 4))
                        );
                        return <SeasonPill key={season} label={labelMap[season]} active={active}/>;
                    })}
                </div>

                {/* AI Recommendation */}
                {showAiRecommendation && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-3">
                        <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1">
                            <SparklesIcon className="w-3.5 h-3.5"/>
                            Рекомендация AI
                        </h4>
                        <div className="flex items-center gap-3">
                            <CircularProgress value={ratingValue} max={10}/>
                            <div className="min-w-0">
                                <p className={`text-sm font-semibold ${ratingColor}`}>{ratingLabel}</p>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{aiProfile!.itemAdvice}</p>
                        <button
                            onClick={() => navigate(`/clothing/${itemId}/ai`)}
                            className="text-xs text-primary dark:text-primary-light hover:underline"
                        >
                            Смотреть AI профиль →
                        </button>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default ItemDetailsTab;
